/**
 * The SEO page's selection in the URL: `?route=<path>` (`*` for Site defaults) and `?lang=<code>`. Pure.
 */
import type { SiteSeoDocument } from './seo-types';

/** The Site defaults row's path. */
export const SITE_DEFAULTS = '*';

export type SeoSelection = { path: string; language: string };

const ROUTE_KEY = 'route';
const LANG_KEY = 'lang';

const withQuery = (q: URLSearchParams): string => {
  const out = q.toString();
  return out === '' ? '' : `?${out}`;
};

/** The route and language `search` names, falling back to Site defaults and the default language for anything the
 *  document or the site does not have (and to Site defaults while there is no document yet). */
export const selectionFromSearch = (
  search: string,
  doc: SiteSeoDocument | null,
  languages: { default: string; all: readonly string[] },
): SeoSelection => {
  const q = new URLSearchParams(search);
  const route = q.get(ROUTE_KEY);
  const lang = q.get(LANG_KEY);
  const known = route !== null && (route === SITE_DEFAULTS || (doc?.routes.some((r) => r.path === route) ?? false));
  return {
    path: known && doc !== null ? route : SITE_DEFAULTS,
    language: lang !== null && languages.all.includes(lang) ? lang : languages.default,
  };
};

/** `search` with the selection written in, every other key kept. */
export const searchWithSelection = (search: string, selection: SeoSelection): string => {
  const q = new URLSearchParams(search);
  q.set(ROUTE_KEY, selection.path);
  q.set(LANG_KEY, selection.language);
  return withQuery(q);
};

/** `search` without a selection (a brand switch: the next brand opens on Site defaults in its default language). */
export const clearedSelection = (search: string): string => {
  const q = new URLSearchParams(search);
  q.delete(ROUTE_KEY);
  q.delete(LANG_KEY);
  return withQuery(q);
};

/** A route is in the URL (Site defaults included): on a phone, the editor shows instead of the route list. */
export const routeChosen = (search: string): boolean => new URLSearchParams(search).has(ROUTE_KEY);

/** `search` without the route, the language and every other key kept (back to the route list on a phone). */
export const searchWithoutRoute = (search: string): string => {
  const q = new URLSearchParams(search);
  q.delete(ROUTE_KEY);
  return withQuery(q);
};
